import ApplicationController from './application_controller';

export default class extends ApplicationController {
  static targets = [
    "map",
    "autocompleteInput",
    "currentLocationText",
    "saveButton",
    "street",
    "suite",
    "city",
    "state",
    "postalCode",
    "country",
    "latitude",
    "longitude",
    "googlePlaceId",
    "formattedAddress",
    "googleAutocompleteJson"
  ];

  connect() {
    console.log("Stimulus controller connected.");

    if (typeof google !== "undefined" && google.maps) {
      this.initMap();
    } else {
      console.log("Waiting for Google Maps API...");
      window.addEventListener("google-maps-loaded", this.initMap.bind(this));
    }

    // Disable save button on load
    this.disableSaveButton();
    this.prepopulateLocation();
  }

  initMap() {
    console.log("Google Maps API is ready.");

    this.map = new google.maps.Map(this.mapTarget, {
      center: { lat: 37.7749, lng: -122.4194 }, // Default center (San Francisco)
      zoom: 12,
    });

    this.initAutocomplete();
  }

  initAutocomplete() {
    if (!this.hasAutocompleteInputTarget) {
      console.error("Autocomplete input field not found.");
      return;
    }

    if (!google.maps.places) {
      console.error("Google Maps Places library is not loaded.");
      return;
    }

    this.autocomplete = new google.maps.places.Autocomplete(this.autocompleteInputTarget, {
      types: ["geocode"],
    });

    this.autocomplete.addListener("place_changed", this.handlePlaceChanged.bind(this));
    console.log("Autocomplete initialized.");
  }

  handlePlaceChanged() {
    const place = this.autocomplete.getPlace();

    if (!place || !place.geometry || !place.geometry.location) {
      console.error("No geometry found for the selected place.");
      return;
    }

    const location = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };
    const addressTitle = place.formatted_address || "Selected Location";

    this.updateMap(location, addressTitle);

    // Update the "Current Location" text and enable the save button
    if (this.hasCurrentLocationTextTarget) {
      this.currentLocationTextTarget.textContent = `Current Location: ${addressTitle}`;
    }
    this.enableSaveButton();
  }

  updateMap(location, title) {
    this.map.setCenter(location);
    this.map.setZoom(15);
    this.marker = new google.maps.Marker({
      position: location,
      map: this.map,
      title,
    });

    // Update hidden fields
    if (this.hasLatitudeTarget) this.latitudeTarget.value = location.lat;
    if (this.hasLongitudeTarget) this.longitudeTarget.value = location.lng;
    if (this.hasFormattedAddressTarget) this.formattedAddressTarget.value = title;

    const place = this.autocomplete.getPlace();
    if (place && place.address_components) {
      let streetNumber = "";
      let streetName = "";

      place.address_components.forEach((component) => {
        const types = component.types;

        if (types.includes("street_number")) {
          streetNumber = component.long_name;
        }

        if (types.includes("route")) {
          streetName = component.long_name;
        }

        if (types.includes("locality") && this.hasCityTarget) {
          this.cityTarget.value = component.long_name;
        }

        if (types.includes("administrative_area_level_1") && this.hasStateTarget) {
          this.stateTarget.value = component.short_name;
        }

        if (types.includes("postal_code") && this.hasPostalCodeTarget) {
          this.postalCodeTarget.value = component.long_name;
        }

        if (types.includes("country") && this.hasCountryTarget) {
          this.countryTarget.value = component.long_name;
        }
      });

      // Combine street number and street name into the street field
      if (this.hasStreetTarget) {
        this.streetTarget.value = `${streetNumber} ${streetName}`.trim();
      }

      if (this.hasGoogleAutocompleteJsonTarget) {
        this.googleAutocompleteJsonTarget.value = JSON.stringify(place);
      }
    }
  }

  disableSaveButton() {
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = true;
    }
  }

  enableSaveButton() {
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = false;
    }
  }
  prepopulateLocation() {
    if (this.hasLatitudeTarget && this.hasLongitudeTarget && this.hasFormattedAddressTarget) {
      const lat = parseFloat(this.latitudeTarget.value);
      const lng = parseFloat(this.longitudeTarget.value);
      const address = this.formattedAddressTarget.value;

      if (lat && lng && address && this.hasGoogleAutocompleteJsonTarget) {
        const googleAutocompleteJson = JSON.parse(this.googleAutocompleteJsonTarget.value);

        const addressComponents = googleAutocompleteJson.address_components || [];

        // Populate nested hidden fields
        addressComponents.forEach((component) => {
          const types = component.types;

          if (types.includes("street_number") && this.hasStreetTarget) {
            this.streetTarget.value = component.long_name;
          }

          if (types.includes("route") && this.hasSuiteTarget) {
            this.suiteTarget.value = component.long_name;
          }

          if (types.includes("locality") && this.hasCityTarget) {
            this.cityTarget.value = component.long_name;
          }

          if (types.includes("administrative_area_level_1") && this.hasStateTarget) {
            this.stateTarget.value = component.short_name; // Use short name for states
          }

          if (types.includes("postal_code") && this.hasPostalCodeTarget) {
            this.postalCodeTarget.value = component.long_name;
          }

          if (types.includes("country") && this.hasCountryTarget) {
            this.countryTarget.value = component.long_name;
          }
        });

        // Update map and marker
        const location = { lat, lng };
        this.map = new google.maps.Map(this.mapTarget, {
          center: location,
          zoom: 15,
        });

        this.marker = new google.maps.Marker({
          position: location,
          map: this.map,
          title: address,
        });

        // Update "Current Location" text
        if (this.hasCurrentLocationTextTarget) {
          this.currentLocationTextTarget.textContent = `Current Location: ${address}`;
        }

        // Enable save button
        this.enableSaveButton();
      }
    }
  }
}
